<template>
  <div class="alarm-view-container">
    <section class="alarm-summary-card">
      <header class="alarm-summary-card__header">
        <div class="alarm-summary-card__title">
          <div
            class="alarm-summary-card__icon"
            :class="hasTriggeredAlarms ? 'alarm-summary-card__icon--alert' : 'alarm-summary-card__icon--ok'"
          >
            <i :class="hasTriggeredAlarms ? 'bi bi-exclamation-triangle-fill' : 'bi bi-check-circle-fill'" />
          </div>
          <span class="alarm-summary-card__title-text">
            {{ $t('components.assetDetails.alarms.emergencyTriggered') }}
          </span>
        </div>
      </header>

      <div class="alarm-summary-card__body">
        <div
          v-if="loading"
          class="alarm-loading-row"
        >
          <div
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          <span>{{ $t('components.assetDetails.alarms.loading') }}</span>
        </div>

        <div
          v-else-if="error"
          class="alarm-error-row"
          role="alert"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ $t('components.assetDetails.alarms.errorLoading') }}</span>
        </div>

        <div
          v-else
          class="alarm-summary-grid"
        >
          <div class="alarm-summary-tile alarm-summary-tile--total">
            <div class="alarm-summary-tile__icon">
              <i class="bi bi-bell-fill" />
            </div>
            <div class="alarm-summary-tile__content">
              <div class="alarm-summary-tile__value">
                {{ summary.total }}
              </div>
              <div class="alarm-summary-tile__label">
                {{ $t('components.assetDetails.alarms.summary.total') }}
              </div>
            </div>
          </div>

          <div class="alarm-summary-tile alarm-summary-tile--triggered">
            <div class="alarm-summary-tile__icon">
              <i class="bi bi-exclamation-circle-fill" />
            </div>
            <div class="alarm-summary-tile__content">
              <div class="alarm-summary-tile__value">
                {{ summary.triggered }}
              </div>
              <div class="alarm-summary-tile__label">
                {{ $t('components.assetDetails.alarms.summary.triggered') }}
              </div>
              <div class="alarm-summary-tile__meta">
                {{ $t('components.assetDetails.alarms.summary.last24h') }}
              </div>
            </div>
          </div>

          <div class="alarm-summary-tile alarm-summary-tile--ok">
            <div class="alarm-summary-tile__icon">
              <i class="bi bi-check-circle-fill" />
            </div>
            <div class="alarm-summary-tile__content">
              <div class="alarm-summary-tile__value">
                {{ summary.notTriggered }}
              </div>
              <div class="alarm-summary-tile__label">
                {{ $t('components.assetDetails.alarms.summary.notTriggered') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="alarm-grid-card">
      <div
        v-if="!loading && alarms.length === 0"
        class="alarm-empty-row"
      >
        <i class="bi bi-check-circle-fill" />
        <span>{{ $t('components.assetDetails.alarms.noAlarms') }}</span>
      </div>

      <template v-else-if="!loading && alarms.length > 0">
        <div class="alarm-grid-card__header">
          <div class="alarm-grid-card__title">
            <div class="alarm-grid-card__title-icon">
              <i class="bi bi-table" />
            </div>
            <span class="alarm-grid-card__title-text">
              {{ $t('components.assetDetails.alarms.title') }}
            </span>
          </div>

          <div class="alarm-grid-card__controls">
            <div class="alarm-search">
              <i class="bi bi-search alarm-search__icon" />
              <input
                v-model="searchValue"
                class="alarm-search__input"
                type="text"
                :placeholder="$t('components.assetDetails.alarms.searchPlaceholder')"
              >
              <button
                v-if="searchValue"
                class="alarm-search__clear"
                @click="searchValue = ''"
              >
                <i class="bi bi-x" />
              </button>
            </div>
          </div>
        </div>

        <div class="alarm-grid-card__body">
          <DxDataGrid
            v-if="filteredAlarms.length > 0"
            :data-source="filteredAlarms"
            :show-borders="false"
            :show-column-lines="false"
            :hover-state-enabled="true"
            :allow-column-reordering="true"
            :allow-column-resizing="true"
            :column-auto-width="true"
            :column-hiding-enabled="true"
            height="auto"
            class="alarm-dx-grid"
          >
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 25, 50]"
              :show-info="true"
              :show-navigation-buttons="true"
              :show-page-size-selector="true"
            />
            <DxFilterRow :visible="false" />

            <DxColumn
              :caption="$t('components.assetDetails.alarms.columns.status')"
              cell-template="status-cell"
              :width="110"
              alignment="center"
            />
            <DxColumn
              :caption="$t('components.assetDetails.alarms.columns.alarm')"
              data-field="description"
              :min-width="200"
              alignment="left"
            />
            <DxColumn
              :caption="$t('components.assetDetails.alarms.columns.code')"
              data-field="param_name"
              :width="150"
              alignment="center"
              cell-template="code-cell"
            />
            <DxColumn
              :caption="$t('components.assetDetails.alarms.columns.counter')"
              data-field="count"
              :width="110"
              alignment="center"
              sort-order="desc"
              cell-template="counter-cell"
            />
            <DxColumn
              :caption="$t('components.assetDetails.alarms.columns.lastHeard')"
              data-field="last_occurrence"
              :width="170"
              alignment="center"
              cell-template="last-heard-cell"
            />

            <template #status-cell>
              <div class="alarm-status-pill">
                <span class="alarm-status-dot" />
                <span class="alarm-status-label">{{ $t('components.assetDetails.alarms.triggered') }}</span>
              </div>
            </template>

            <template #code-cell="{ data }">
              <span class="alarm-acronym-cell">{{ data.data.param_name.replace('DKN_LMS_', '') }}</span>
            </template>

            <template #counter-cell="{ data }">
              <span class="alarm-counter-cell">{{ data.data.count }}</span>
            </template>

            <template #last-heard-cell="{ data }">
              <span class="alarm-last-heard-cell">
                {{ data.data.last_occurrence ? relativeTime(data.data.last_occurrence) : '—' }}
              </span>
            </template>
          </DxDataGrid>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxPager,
  DxPaging,
  DxFilterRow,
} from 'devextreme-vue/data-grid'

interface AlarmEntry {
  param_name: string
  description: string
  count: number
  last_occurrence: number | null
}

export default defineComponent({
  name: 'DknAlarmTable',
  components: { DxDataGrid, DxColumn, DxPager, DxPaging, DxFilterRow },
  props: {
    alarms: {
      type: Array as () => AlarmEntry[],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String as () => string | null,
      default: null,
    },
    totalAlarms: {
      type: Number,
      default: 0,
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      searchValue: '',
    }
  },
  computed: {
    hasTriggeredAlarms(): boolean {
      return this.alarms.length > 0
    },
    summary(): { total: number; triggered: number; notTriggered: number } {
      return {
        total:        this.totalAlarms,
        triggered:    this.alarms.length,
        notTriggered: this.totalAlarms - this.alarms.length,
      }
    },
    filteredAlarms(): AlarmEntry[] {
      const q = this.searchValue.trim().toLowerCase()
      if (!q) return this.alarms
      return this.alarms.filter(
        (a) => a.description.toLowerCase().includes(q) || a.param_name.toLowerCase().includes(q),
      )
    },
  },
  methods: {
    relativeTime(ts: number): string {
      const diff = Math.floor(Date.now() / 1000) - ts
      if (diff < 60)    return this.t('components.assetDetails.alarms.lastHeardJustNow')
      if (diff < 3600)  return this.t('components.assetDetails.alarms.lastHeardMinutes', { n: Math.floor(diff / 60) })
      if (diff < 86400) return this.t('components.assetDetails.alarms.lastHeardHours',   { n: Math.floor(diff / 3600) })
      return this.t('components.assetDetails.alarms.lastHeardDays', { n: Math.floor(diff / 86400) })
    },
  },
})
</script>

<style lang="scss">
@import '@/assets/scss/dkn-alarm-view.scss';
</style>
